import React, { useState, useEffect } from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline'; // Example icon

function UserManagementView({ token, setError }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingEmail, setSendingEmail] = useState(null); // Track which user's email is being sent

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(''); // Clear previous errors
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch users.');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while fetching users.');
      setUsers([]); // Clear users on error
    } finally {
      setLoading(false);
    }
  };

  // --- Email Sending Placeholder ---
  // This needs a corresponding backend endpoint (e.g., POST /api/admin/users/:userId/send-email)
  const handleSendEmail = async (userId, emailType = 'welcome') => {
    if (sendingEmail) return; // Prevent multiple clicks
    if (!window.confirm(`Send ${emailType} email to this user?`)) return;

    setSendingEmail(userId); // Indicate sending started for this user
    setError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/${userId}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ emailType }), // Send type in body
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to send ${emailType} email.`);
      }

      // Optionally show a success message (e.g., using a toast notification library)
      // Use the message from the backend simulation for now
      const result = await response.json();
      alert(result.message || `${emailType.charAt(0).toUpperCase() + emailType.slice(1)} email sent successfully!`);


    } catch (err) {
      console.error(err);
      setError(err.message || `An error occurred while sending the ${emailType} email.`);
    } finally {
      setSendingEmail(null); // Reset sending state
    }
  };
  // --- End Email Sending Placeholder ---

  const filteredUsers = users.filter(user =>
    (user.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (user.role?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white p-4 md:p-6 rounded-lg">

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name, email, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {loading ? (
        <p className="text-center text-gray-500 py-4">Loading users...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.role === 'admin' ? 'bg-red-100 text-red-800' :
                        user.role === 'pro' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800' // Default for 'student' or others
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {/* Calculate progress based on completedChapters array length */}
                      {user.completedChapters ? `${user.completedChapters.length} Chapters` : '0 Chapters'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {/* Example Email Button */}
                      <button
                        onClick={() => handleSendEmail(user._id, 'welcome')}
                        disabled={sendingEmail === user._id}
                        title="Send Welcome Email"
                        className={`p-1 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {sendingEmail === user._id ? 'Sending...' : <EnvelopeIcon className="h-5 w-5" />}
                      </button>
                      {/* Add other actions like 'Edit Role', 'View Details', 'Send Reminder Email' etc. */}
                      {/* Example: Send Reminder Email Button */}
                      {/*
                      <button
                        onClick={() => handleSendEmail(user._id, 'reminder')}
                        disabled={sendingEmail === user._id}
                        title="Send Reminder Email"
                        className={`p-1 rounded text-orange-600 hover:text-orange-800 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {sendingEmail === user._id ? 'Sending...' : <BellAlertIcon className="h-5 w-5" />} // Assuming BellAlertIcon is imported
                      </button>
                      */}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    No users found matching your search criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default UserManagementView;